import type { AddToCartTypes } from '@maestro/commerce-blocks/components/product-card/product-card.types'
import type {
  CheckoutType,
  CmsImages,
  CmsPlaceholder,
  CmsPlaceholderAccountPage,
  ColorConfig,
  CompanySwitcherType,
  ConsentConfiguration,
  FavoritesAdvancedVariant,
  FavoritesImplementation,
  FilterBarConfig,
  InternalCheckoutConfig,
  InternalCheckoutSummaryConfig,
  Locales,
  NavigationVariant,
  OrderAddressB2BConfig,
  OrderInvoiceListConfig,
  OrderProductListB2BConfig,
  ProductAccordionSection,
  ProductCmsSection,
  ProductIdentifierItem,
} from '@maestro/commerce-blocks/theme/theme.types'
import type { CmsComponentConfig } from '@maestro/commerce-blocks/utils/image.utils'
import type { SubscriptionIntervalOption } from '@maestro/data-layer'

import { PageType } from '@maestro/core'
import { envBoolean, envNumber } from '@maestro/core/config/env'

const urls = {
  articles: '/inspirasjon',
  bonusPointsHistory: '/bonus-points-history',
  brands: '/brands',
  campaigns: '/campaigns',
  cart: '/cart',
  checkout: '/checkout',
  checkoutSuccess: '/checkout/success',
  customerClub: '/customer-club',
  favoriteList: '/favorite-list',
  favoriteLists: '/favorite-lists',
  favorites: '/favorites',
  findStore: '/butikker',
  guestReturnOrComplaint: '/order-return-or-complaint/guest',
  home: '/',
  invoices: '/invoices',
  login: '/login',
  logout: '/logout',
  memberAdvantages: '/member-advantages',
  myAccount: '/my-account',
  offers: '/offers',
  order: '/order',
  orderReturnOrComplaint: '/order-return-or-complaint',
  orders: '/order-history',
  profile: '/account-information',
  register: '/register',
  registerCheckEmail: '/register-check-email',
  registerConfirmation: '/register-confirmation',
  resetPassword: '/reset-password',
  search: '/search',
  shoppingList: '/shopping-list',
  shoppingLists: '/shopping-lists',
  sitemapCategories: '/sitemap',
  subscriptions: '/subscriptions',
  wishlist: '/wishlist',
}
const placeholderNoImageSrc = '/assets/placeholder_no-image.svg'
const localAssetImages = [placeholderNoImageSrc]

const isB2BStoreType = process.env.NEXT_PUBLIC_STORE_TYPE === 'b2b'
const isEnabledSubscriptions = envBoolean(
  process.env.NEXT_PUBLIC_SUBSCRIPTIONS_ENABLED
)
const isEnabledClientSideStockFetch = envBoolean(
  process.env.NEXT_PUBLIC_CLIENT_SIDE_STOCK_ENABLED
)
const isEnabledClientSidePriceFetch = envBoolean(
  process.env.NEXT_PUBLIC_CLIENT_SIDE_PRICE_ENABLED
)
const favoritesImplementation = (process.env
  .NEXT_PUBLIC_FAVORITES_IMPLEMENTATION || 'simple') as FavoritesImplementation
const favoritesAdvancedVariant = (process.env
  .NEXT_PUBLIC_FAVORITES_ADVANCED_VARIANT ||
  (isB2BStoreType ? 'b2b' : 'b2c')) as FavoritesAdvancedVariant

const theme = {
  site: {
    locale: (process.env.NEXT_PUBLIC_LOCALE || 'en-US') as Locales,
    timeZone: process.env.NEXT_PUBLIC_SITE_TIMEZONE || 'GMT',
    country: 'NO',
    secure: false,
    title: 'Maestro',
    disableIndexing: false,
    showSiteErrorDetails: false,
    logo: {
      width: 113,
      height: 33,
    },
    isB2BStoreType,
    isContentOnly: false, // cms pages only, no commerce platform features
    isRecommendationsSampleData: envBoolean(
      process.env.NEXT_PUBLIC_RECOMMENDATIONS_SAMPLE_DATA_ENABLED
    ), // display sample data in recommendations instead of DY or other recommendation engine data
    consentConfig: {
      consentGateEnabled: true,
      consents: {
        all: 'preferences,statistics,marketing',
        marketing: 'marketing',
        analytical: 'statistics',
        functional: 'preferences',
      },
      embeddedVideoWrapperClass: 'youtube-embed-wrapper',
    },
    checkoutType: (isB2BStoreType ? 'internal' : 'kustom') as CheckoutType,
  },
  productPicker: {
    resultsPerPage: 25, // products listed in the product search dropdown.
  },
  priceConfig: {
    currencyIso: process.env.NEXT_PUBLIC_SITE_CURRENCY || 'NOK',
    currencyName: 'kroner', // used for ScreenReader accessibility texts
    showDecimals: true,
    prefix: '',
    suffix: ',-',
    separator: ',',
    currencySymbol: 'kr',
    showThousandSeparator: true,
    thousandSeparator: ' ',
    showFormattedPrice: false,
    showOnlyForLoggedIn: false,
    showTaxLabel: isB2BStoreType,
  },
  urls,
  header: {
    behavior: {
      mobile: 'sticky', // 'sticky' | 'fixed' | 'relative'
      desktop: 'relative',
    },
    isTopbarEnabled: isB2BStoreType,
    isCompanySwitcherEnabled: isB2BStoreType,
    companySwitcherType: (process.env.NEXT_PUBLIC_COMPANY_SWITCHER_TYPE ||
      'company') as CompanySwitcherType,
    isShippingAddressSwitcherReady: true,
    companySwitcher: {
      searchEnabled: false,
    },
    isEnabledUserInfo: isB2BStoreType,
    userInfo: {
      isEnabledCompany: false,
      isEnabledSignOut: false,
      isEnabledUserName: true,
    },
  },
  announcementBar: {
    isAboveHeader: false,
  },
  freeShippingBar: {
    isVisibleFreeShippingBar: true,
  },
  usp: {
    isUspEnabled: true,
  },
  recommendations: {
    isRecommendationsEnabled: true,
  },
  navigation: {
    openOnClick: true, // desktop submenu activated on 'onClick' or on 'onHover'
    variant: (process.env.NEXT_PUBLIC_NAVIGATION_VARIANT ||
      'standard') as NavigationVariant, // 'standard' in-row menu or 'foldout' button-triggered panel
  },
  search: {
    foundProductsCarouselSize: 5,
    resultsPerPageSearch: 120,
    resultsPerPagePLP: 120,
    suggestionsPerPage: 6,
    popularSuggestions: envNumber(
      process.env.NEXT_PUBLIC_SEARCH_SUGGESTIONS_POPULAR,
      4
    ),
    articlesPerPageSearch: 18,
    resultsPerPageMemberProductListing: 10,
    searchNotFound: {
      cmsBlockIdentifiers: ['search-not-found'],
    },
    searchBar: {
      fetchSuggestionsDelay: 250,
      searchDelay: 600,
      isEnabledSearchBySku: false,
      isEnabledDirectHitQuantityPicker: false,
      showDirectHitActionOnlyForLoggedIn: false,
    },
    productSearchDefaultFilters: [] as string[],
    categorySearchDefaultFilters: [] as string[], // for example ['rootCategoryIds:<categoryId>']
    brandFilterKey: 'brand_id',
    categoryFilterKey: 'categoryIds',
    campaignFilterKey: 'campaignIds',
    campaignsOverviewFilterKey: 'hasCampaign:true',
    dynamicAssortmentFilterKey: undefined, // for example 'agreements' (B2B stores)
    memberCampaignFilterKey: 'hasMemberCampaign:true',
  },
  filter: {
    seoKeys: {
      category: 'category',
      text: 'text',
      sortBy: 'sort',
    },
    visibleCheckboxFacet: 20,
    priceFacet:
      process.env.NEXT_PUBLIC_FILTER_PRICE_FACET || 'price.NOK.default',
    sortByLabel: false, // sort facets by their labels (alphabetically) for sanity mode; false = use Sanity config order
    valueSort: 'alphabetical' as 'alphabetical' | 'count', // sort facet values: 'alphabetical' or 'count' for sanity mode
    categoryFilter: {
      hierarchicalCategoryFacet: 'categories.level0',
      hierarchicalCategoryAttributeTemplate: 'categories.level',
      hierarchicalCategoryFacetKey: 'hierarchicalMenu[categories.level0]',
      hierarchicalCategorySeparator: ' /// ',
    },
    secondaryFiltersList: JSON.parse(
      process.env.NEXT_PUBLIC_ALGOLIA_SECONDARY_FILTERS_LIST || '[]'
    ) as string[], // list of facet names to be displayed in the secondary row of filters
    isVisibleSortButton: true,
  },
  pagination: {
    visibleElements: {
      mobile: 5,
      desktop: 9,
    },
  },
  cms: {
    navigation: {
      header: process.env.NEXT_PUBLIC_HEADER_NAVIGATION || 'header-navigation',
      footer: 'footer-navigation',
      mobileMenuShortcuts: 'mobile-menu-shortcuts',
    },
    banners: {
      isFullBleed: false,
    },
    articles: {
      title: 'Inspiration',
      urlPath: urls.articles,
      categoryDepth: 4,
      pageLimit: 18,
      showArticleShortcuts: false,
    },
    page: {
      parentPageDepth: 2,
    },
    quantityCounterMaxValue: 99,
    isCmsCompanyHomePageEnabled: envBoolean(
      process.env.NEXT_PUBLIC_CMS_COMPANY_HOME_PAGE_ENABLED
    ),
    isCmsCompanyBlockEnabled: envBoolean(
      process.env.NEXT_PUBLIC_CMS_COMPANY_BLOCKS_ENABLED
    ),
  },
  store: {
    availableInStoresNearYouRadius: 20, //km
    title: 'Find store',
    urlPath: urls.findStore,
    isMultiItemLayout: true,
    isSingleSelectFacet: true,
    markerIcon: 'brand-mark',
    geoLocalizedStoresCount: 5,
    cmsPlaceholders: [
      {
        id: 'top',
        contentSlot: 'store-top',
        skeletonHeight: 0,
      },
      {
        id: 'bottom',
        contentSlot: 'store-bottom',
        skeletonHeight: 0,
      },
    ] as CmsPlaceholder[],
    nearestStoresInStockOnly: true,
    visibleFiltersCount: 3,
  },
  product: {
    details: {
      cmsSections: [
        {
          id: 'delivery-and-returns',
          title: 'Frakt og levering',
          blockIdentifier: 'delivery-and-returns',
        },
      ] as ProductCmsSection[],
      cmsPlaceholders: [
        // Example for adding CMS Block before recommendations (static identifier):
        // {
        //   id: 'beforeRecommendations',
        //   blockIdentifier: 'test-cms-block',
        //   skeletonHeight: 438,
        // },
        // Example for adding CMS Block after reviews (dynamic identifier):
        {
          id: 'beforeRecommendations',
          contentSlot: 'product-top',
          skeletonHeight: 0,
        },
        {
          id: 'afterRecommendations',
          contentSlot: 'product-bottom',
          skeletonHeight: 0,
        },
      ] as CmsPlaceholder[],
      stockStatus: {
        displayStockInVariantSelector: true,
      },
      displayedAttributes: [
        'name',
        'sku',
        'color',
        'size',
        'style',
        'brand',
        'designer',
        'gender',
        'season',
        'width',
        'height',
        'depth',
      ] satisfies string[],
      variants: {
        // Multi-attribute (Color+Size) selection; off by default for backward compatibility.
        enableMultiVariant: false,
        // Backend name of the color axis (e.g. SAP qualifier `style`); renders it as swatches
        // and is aliased by the SAP converter to `colorAttributeName`. Empty = no color axis.
        colorAttributeQualifier: '',
        // Fields requested on variant-details refresh; empty = backend's own default shape.
        variantDetailsFields: [] as string[],
        // Platform-neutral attribute name every converter emits for a variant's color.
        colorAttributeName: 'color',
      },
      accordion: {
        displayAsDialog: true,
      } satisfies ProductAccordionSection,
      displayProductRatingBelowTitle: true,
      displayShowMoreButtonProductDescription: true,
      isDocumentsEnabled: isB2BStoreType,
      isQuantityPickerEnabled: isB2BStoreType,
      // Packaging/sales unit shown next to price on PDP, disabled by default - projects opt in.
      isEnabledPackagingUnit: false,
      priceRequest: {
        isEnabled: false,
      },
    },
    isReviewsEnabled: true,
    showReviewSummary: false,
    reviewsRatingDecimalPlaces: 1,
    addToCartDisplayType: {
      productListing: 'none',
      productList: 'button',
      searchTopFoundProducts: 'none',
      cmsProducts: 'none',
      wishlist: 'none',
      wishlistList: 'icon',
      recommendations: 'none',
      recommendationsList: 'button',
    } as Record<string, AddToCartTypes>,
    showAddToCartOnlyForLoggedIn: false,
    /** @deprecated Unused since ECOM-18959. Use `isOutOfStockNotificationEnabled`. Removed in the next major. */
    storeOutOfStockSubscriptions: true,
    isOutOfStockNotificationEnabled: true,
    isAddToCartProductStockValidationEnabled: true,
    fetchStockClientSide: isEnabledClientSideStockFetch,
    displayStockStatus: {
      productCardDefault: false,
      cartItemDontForget: false,
      cmsProducts: false,
      recommendations: false,
      recommendationsList: false,
      searchTopFoundProducts: false,
      wishlist: false,
      productList: false,
      searchProductList: false,
    },
    enableClientSideStockFetching: {
      details: isEnabledClientSideStockFetch,
      cartItemDontForget: isEnabledClientSideStockFetch,
      cmsProducts: isEnabledClientSideStockFetch,
      recommendations: isEnabledClientSideStockFetch,
      recommendationsList: isEnabledClientSideStockFetch,
      wishlist: isEnabledClientSideStockFetch,
      productList: isEnabledClientSideStockFetch,
      searchProductList: isEnabledClientSideStockFetch,
    },
    displayPrice: {
      productCardDefault: true,
      cartItemDontForget: true,
      cmsProducts: true,
      recommendations: true,
      recommendationsList: true,
      searchTopFoundProducts: true,
      wishlist: true,
      productList: true,
      searchProductList: true,
    },
    enableClientSidePriceFetching: {
      details: isEnabledClientSidePriceFetch,
      cartItemDontForget: isEnabledClientSidePriceFetch,
      cmsProducts: isEnabledClientSidePriceFetch,
      recommendations: isEnabledClientSidePriceFetch,
      recommendationsList: isEnabledClientSidePriceFetch,
      wishlist: isEnabledClientSidePriceFetch,
      productList: isEnabledClientSidePriceFetch,
      searchProductList: isEnabledClientSidePriceFetch,
    },
    isVisibleBrand: true,
    isVisibleSku: isB2BStoreType,
    identifierItemsPDP: [
      { labelKey: 'product.product-number-shorthand', field: 'sku' },
    ] as ProductIdentifierItem[],
    identifierItemsListings: [
      { labelKey: 'product.product-number-shorthand-listings', field: 'sku' },
    ] as ProductIdentifierItem[],
  },
  category: {
    cmsPlaceholders: [
      {
        id: 'top',
        contentSlot: 'category-top',
        skeletonHeight: 0,
      },
      {
        id: 'bottom',
        contentSlot: 'category-bottom',
        skeletonHeight: 0,
      },
    ] as CmsPlaceholder[],
  },
  brand: {
    cmsPlaceholders: [
      {
        id: 'top',
        contentSlot: 'brand-default',
        skeletonHeight: 0,
      },
    ] as CmsPlaceholder[],
  },
  brandsOverview: {
    cmsPlaceholders: [
      {
        id: 'top',
        blockIdentifier: 'brands-overview-top-cms-block',
        skeletonHeight: 0,
      },
    ] as CmsPlaceholder[],
  },
  campaign: {
    cmsPlaceholders: [
      {
        id: 'top',
        contentSlot: 'campaign-default',
        skeletonHeight: 0,
      },
    ] as CmsPlaceholder[],
  },
  campaignsOverview: {
    cmsPlaceholders: [
      {
        id: 'top',
        blockIdentifier: 'campaigns-overview-top-cms-block',
        skeletonHeight: 0,
      },
    ] as CmsPlaceholder[],
  },
  cart: {
    bounce: 500,
    isSlidein: false,
    expires: 90, // days
    oneDayInMilliseconds: 86400000,
    isClickAndCollectEnabled: !isB2BStoreType,
    isCouponCodesEnabled: true,
    showDeliveryMethodsTabsTop: false,
    showDeliveryMethodsTabsSidebar: true,
    showVippsExpressCheckout: envBoolean(
      process.env.NEXT_PUBLIC_VIPPS_EXPRESS_CHECKOUT_ENABLED
    ),
    showNewestFirst: true, // Display newest cart entries first (highest entryNumber first)
    saveAndRestoreCartTab: false,
    isEnabledImportProducts: false,
    isEnabledExportCart: false,
    useLowerCaseVariantName: true,
    usePrecalculatedPrices: isB2BStoreType, // For B2b store use the prices (formatted values) calculated on BE
  },
  company: {
    cmsPlaceholders: [
      {
        id: 'company',
        contentSlot: 'company',
        skeletonHeight: 0,
      },
    ] as CmsPlaceholder[],
  },
  notification: {
    timeout: 5000,
    addedToCartTimeout: 10000,
    horizontal: 'right',
    vertical: 'top',
    autoClose: true,
  },
  images: {
    // ImageProduct config BEGIN
    converter: process.env.NEXT_PUBLIC_MAESTRO_IO!, // typically 'https://site-url/io/'
    defaultImageRatio: 1,
    thumbnailWidth: 80,
    thumbnailHeight: 80,
    localAssetImages,
    placeholderNoImageSrc,
    // Global image defaults
    quality: 75,
    fit: 'bounds' as const,
    // ImageProduct config END

    // ImageProduct sizes BEGIN
    // ProductCard - category, search, wishlist page; the default size
    plp: {
      width: 340,
      height: 340,
      srcSet: [220, 340, 440, 660],
      sizes: [{ maxWidth: '767px', size: '50vw' }, { size: '340px' }],
    },
    // Product page mobile gallery
    pdpMobile: {
      width: 440,
      height: 440,
      srcSet: [390, 440, 780],
    },
    // Product page desktop gallery
    pdpDesktop: {
      width: 660,
      height: 660,
      srcSet: [440, 660, 880, 1320],
      sizes: [{ maxWidth: '1920px', size: '44vw' }, { size: '880px' }],
    },
    // Product page gallery zoom
    zoom: {
      width: 1320,
      height: 1320,
      srcSet: [880, 1320, 1760, 1920],
      sizes: [
        { maxWidth: '767px', size: '200vw' },
        { maxWidth: '1920px', size: '100vw' },
        { size: '1920px' },
      ],
    },
    // Product page gallery thumbnails - widths for DPR 1x and 2x
    pdpThumbnail: {
      width: 96,
      height: 96,
      srcSet: [76, 96, 152, 192],
      sizes: [{ size: '96px' }],
    },
    pdpVariantColor: {
      width: 96,
      height: 96,
      srcSet: [96, 192],
      sizes: [{ maxWidth: '767px', size: '76px' }, { size: '96px' }],
    },
    // Cart list item - widths for DPR 1x and 2x
    cartList: {
      width: 96,
      height: 96,
      srcSet: [96, 192],
      sizes: [{ size: '96px' }],
    },
    // Product list item - widths for DPR 1x and 2x
    productList: {
      width: 80,
      height: 80,
      srcSet: [80, 160],
      sizes: [{ size: '80px' }],
    },
    // Product list item, large variant - widths for DPR 1x and 2x.
    productListLarge: {
      width: 160,
      height: 160,
      srcSet: [160, 320],
      sizes: [{ size: '160px' }],
    },
    // Wishlist list item - widths for DPR 1x and 2x
    wishlistList: {
      width: 90,
      height: 90,
      srcSet: [90, 180],
      sizes: [{ size: '90px' }],
    },
    // Order history overview
    orderHistoryOverview: {
      width: 220,
      height: 220,
      srcSet: [220, 340, 440, 660],
      sizes: [{ maxWidth: '896px', size: '45vw' }, { size: '440px' }],
    },
    // Order history details
    orderHistoryDetails: {
      width: 80,
      height: 80,
      srcSet: [80, 128, 160, 256],
      sizes: [{ maxWidth: '767px', size: '80px' }, { size: '128px' }],
    },
    // ImageProduct config END

    articleAuthor: {
      default: {
        desktop: {
          width: 74,
          height: 74,
          srcSet: [74, 148],
          sizes: [{ size: '74px' }],
          fit: 'crop',
        },
        mobile: {
          width: 74,
          height: 74,
          srcSet: [74, 148],
          sizes: [{ size: '74px' }],
          fit: 'crop',
        },
      },
    } satisfies CmsComponentConfig,
    articleImage: {
      'full-width': {
        desktop: {
          width: 1920,
          height: 640,
          srcSet: [840, 1280, 1536, 1728],
          fit: 'crop',
        },
        mobile: {
          width: 440,
          height: 360,
          srcSet: [390, 440, 780],
          fit: 'crop',
        },
      },
      wide: {
        desktop: {
          width: 1728,
          height: 576,
          srcSet: [744, 1184, 1440, 1728], // images for screen widths [840, 1280, 1536, 1920]
          sizes: [
            { maxWidth: '800px', size: 'calc(100vw - 64px)' },
            { maxWidth: '1600px', size: 'calc(100vw - 96px)' },
            { size: '1728px' },
          ],
          fit: 'crop',
        },
        mobile: {
          width: 440,
          height: 360,
          srcSet: [390, 440, 780],
          fit: 'crop',
        },
      },
      narrow: {
        desktop: {
          width: 800,
          height: 0, // keep the original aspect ratio
          srcSet: [800, 1200],
          sizes: [{ size: '800px' }],
        },
        mobile: {
          width: 390,
          height: 0,
          srcSet: [390, 440, 780],
          sizes: [{ size: 'calc(100vw - 48px)' }],
        },
      },
    } satisfies CmsComponentConfig,
    articleTeasers: {
      default: {
        desktop: {
          width: 560,
          height: 400,
          srcSet: [380, 580, 708, 852],
          sizes: [
            { maxWidth: '1600px', size: 'calc((100vw - 120px) / 2)' },
            { size: '852px' },
          ],
          fit: 'crop',
        },
        mobile: {
          width: 440,
          height: 275,
          srcSet: [342, 392, 492, 780],
          sizes: [
            { maxWidth: '540px', size: 'calc(100vw - 48px)' },
            { size: 'calc(100vw - 48px)' },
          ],
          fit: 'crop',
        },
      },
    } satisfies CmsComponentConfig,
    articlesListing: {
      default: {
        desktop: {
          width: 560,
          height: 400,
          srcSet: [280, 380, 464, 560],
          sizes: [
            { maxWidth: '1600px', size: 'calc((100vw - 144px) / 3)' },
            { size: '560px' },
          ],
          fit: 'crop',
        },
        mobile: {
          width: 440,
          height: 275,
          srcSet: [342, 392, 492, 780],
          sizes: [
            { maxWidth: '540px', size: 'calc(100vw - 48px)' },
            { size: 'calc(50vw - 28px)' },
          ],
          fit: 'crop',
        },
      },
    } satisfies CmsComponentConfig,
    articlesPreview: {
      default: {
        desktop: {
          width: 140,
          height: 140,
          srcSet: [140, 280],
          sizes: [{ size: '140px' }],
          fit: 'crop',
        },
        mobile: {
          width: 148,
          height: 98,
          srcSet: [148, 296],
          sizes: [{ size: '148px' }],
          fit: 'crop',
        },
      },
    } satisfies CmsComponentConfig,
    // Store details image
    store: {
      width: 840,
      height: 640,
    },

    cms: {
      config: {
        dpr: 1,
        //sharp: 14,
        quality: 75,
        fit: 'min',
      },
      default: {
        default: {
          desktop: { width: 1920, height: 1080 },
          mobile: { width: 375, height: 300 },
        },
      },
      ballLink: {
        default: {
          desktop: {
            width: 70,
            height: 70,
            srcSet: [70, 140],
            sizes: [{ size: '70px' }],
            fit: 'max',
          },
          mobile: {
            width: 70,
            height: 70,
            srcSet: [70, 140],
            sizes: [{ size: '70px' }],
            fit: 'max',
          },
        },
      },
      bannerBadgeImages: {
        default: {
          desktop: {
            width: 80,
            height: 80,
            srcSet: [80, 160],
            sizes: [{ size: '80px' }],
            fit: 'max',
          },
          mobile: {
            width: 56,
            height: 56,
            srcSet: [56, 112],
            sizes: [{ size: '56px' }],
            fit: 'max',
          },
        },
      },
      banners: {
        'full-width': {
          desktop: {
            width: 1344,
            height: 424,
            srcSet: [
              { width: 744, height: 448 },
              { width: 1184, height: 674 },
              { width: 1440, height: 515 },
              { width: 1728, height: 509 },
            ], // images for screen widths [840, 1280, 1536, 1920]
            sizes: [
              { maxWidth: '800px', size: 'calc(100vw - 64px)' },
              { maxWidth: '1600px', size: 'calc(100vw - 96px)' },
              { size: '1728px' },
            ],
          },
          mobile: {
            width: 390,
            height: 260,
            srcSet: [
              { width: 390, height: 260 },
              { width: 440, height: 293 },
              { width: 780, height: 460 },
            ],
          },
        },
        left: {
          desktop: {
            width: 672,
            height: 424,
            srcSet: [
              { width: 592, height: 713 },
              { width: 720, height: 515 },
              { width: 864, height: 509 },
            ],
            sizes: [
              { maxWidth: '1600px', size: 'calc(50vw - 48px)' },
              { size: '864px' },
            ],
          },
          mobile: {
            width: 390,
            height: 260,
            srcSet: [
              { width: 390, height: 260 },
              { width: 440, height: 293 },
              { width: 780, height: 460 },
            ],
          },
        },
        right: {
          desktop: {
            width: 672,
            height: 424,
            srcSet: [
              { width: 592, height: 713 },
              { width: 720, height: 515 },
              { width: 864, height: 509 },
            ],
            sizes: [
              { maxWidth: '1600px', size: 'calc(50vw - 48px)' },
              { size: '864px' },
            ],
          },
          mobile: {
            width: 390,
            height: 260,
            srcSet: [
              { width: 390, height: 260 },
              { width: 440, height: 293 },
              { width: 780, height: 460 },
            ],
          },
        },
        category: {
          desktop: {
            width: 124,
            height: 124,
            srcSet: [124, 248],
            sizes: [{ size: '124px' }],
          },
          mobile: {
            width: 112,
            height: 112,
            srcSet: [112, 224],
            sizes: [{ size: '112px' }],
          },
        },
      },
      bigContentBanner: {
        default: {
          desktop: {
            width: 672,
            height: 560,
            srcSet: [
              { width: 592, height: 942 },
              { width: 720, height: 681 },
              { width: 864, height: 672 },
            ],
            sizes: [
              { maxWidth: '1600px', size: 'calc(50vw - 48px)' },
              { size: '864px' },
            ],
          },
          mobile: {
            width: 390,
            height: 210,
            srcSet: [
              { width: 390, height: 210 },
              { width: 440, height: 237 },
              { width: 780, height: 420 },
            ],
          },
        },
      },
      imageWithLink: {
        default: {
          desktop: { width: 300, height: 100, fit: 'max' },
          mobile: { width: 300, height: 100, fit: 'max' },
        },
      },
      images: {
        'full-width': {
          desktop: {
            width: 1920,
            height: 640,
            srcSet: [840, 1280, 1536, 1728],
          },
          mobile: { width: 440, height: 360, srcSet: [390, 440, 780] },
        },
        wide: {
          desktop: {
            width: 1728,
            height: 576,
            srcSet: [744, 1184, 1440, 1728], // images for screen widths [840, 1280, 1536, 1920]
            sizes: [
              { maxWidth: '800px', size: 'calc(100vw - 64px)' },
              { maxWidth: '1600px', size: 'calc(100vw - 96px)' },
              { size: '1728px' },
            ],
          },
          mobile: { width: 440, height: 360, srcSet: [390, 440, 780] },
        },
        narrow: {
          desktop: {
            width: 800,
            height: 0, // keep the original aspect ratio
            srcSet: [800, 1200],
            sizes: [{ size: '800px' }],
          },
          mobile: {
            width: 390,
            height: 0,
            srcSet: [390, 440, 780],
            sizes: [{ size: 'calc(100vw - 48px)' }],
          },
        },
      },
      memberAdvantages: {
        default: {
          desktop: {
            width: 55,
            height: 55,
            srcSet: [55, 110],
            sizes: [{ size: '55px' }],
            fit: 'max',
          },
          mobile: {
            width: 55,
            height: 55,
            srcSet: [55, 110],
            sizes: [{ size: '55px' }],
            fit: 'max',
          },
        },
      },
      tiles: {
        default: {
          desktop: {
            width: 128,
            height: 128,
            srcSet: [128, 256],
            sizes: [{ size: '128px' }],
            fit: 'max',
          },
          mobile: {
            width: 128,
            height: 128,
            srcSet: [128, 256],
            sizes: [{ size: '128px' }],
            fit: 'max',
          },
        },
      },
    } satisfies CmsImages,
    isEnabledVariantImagesInGallery: false,
  },
  colorConfig: {
    colorPreviewMapping: {
      red: { name: 'Red', value: 'rgb(255, 0, 0)' },
      green: { name: 'Green', value: 'rgb(0, 255, 0)' },
      blue: { name: 'Blue', value: 'rgb(0, 0, 255)' },
      black: { name: 'Black', value: 'rgb(0, 0, 0)' },
      white: { name: 'White', value: 'rgb(255, 255, 255)' },
      'black and white geometric pattern': {
        name: 'Black and White Geometric Pattern',
        value: 'url("/assets/patterns/black_and_white.png")',
      },
      fallback: { name: 'Fallback', value: 'rgb(0, 0, 0)' },
    },
  } as ColorConfig,
  providers: {
    categoryHeader: {
      maxItems: 10,
    },
  },
  accountPage: {
    accountRootSection: {
      labelKey: 'account.my-account',
      url: urls.myAccount,
    },
    menuItems: [
      {
        labelKey: 'account.orders',
        url: urls.orders,
        icon: 'package',
      },
      {
        labelKey: 'account.invoices',
        url: urls.invoices,
        icon: 'label',
      },
      ...(isEnabledSubscriptions
        ? [
            {
              labelKey: 'account.subscriptions',
              url: urls.subscriptions,
              icon: 'calendar',
            },
          ]
        : []),
      {
        labelKey:
          favoritesImplementation === 'simple'
            ? 'account.favorites'
            : favoritesAdvancedVariant === 'b2b'
              ? 'account.shopping-lists'
              : 'account.favorites',
        url:
          favoritesImplementation === 'simple'
            ? urls.wishlist
            : favoritesAdvancedVariant === 'b2b'
              ? urls.shoppingLists
              : urls.favorites,
        icon: 'heart-empty',
      },
      {
        labelKey: 'account.profile',
        url: urls.profile,
        icon: 'user',
      },
      {
        labelKey: 'account.offers',
        url: urls.offers,
        icon: 'percent',
      },
    ],
    loyaltyHeaderUrl: urls.memberAdvantages,
    invoiceHistory: {
      pageSize: 25, // Number of invoices to show per page
    },
    orderHistory: {
      withProductImages: true,
      maxProductImages: 8,
      maxImagesDesktop: 8,
      maxImagesMobile: 6,
      notFoundCmsId: 'orders-not-found',
      pageSize: 25, // Number of orders to show per page
      showViewDetailsBtn: true,
      showBuyAgainBtn: false,
      isOrderSearchEnabled: isB2BStoreType,
      isEditOrderEnabled: isB2BStoreType,
      skeletonFiltersLength: 3,
      filterBar: {
        filters: [{ id: 'option' }, { id: 'dateRange' }, { id: 'search' }],
      } as FilterBarConfig,
    },
    subscriptions: {
      isEnabled: isEnabledSubscriptions,
      pageSize: 25, // Number of subscriptions to show per page
      productSearchFilters: '',
      intervalOptions: [
        { value: 1, unit: 'week', days: 7 },
        { value: 2, unit: 'week', days: 14 },
        { value: 3, unit: 'week', days: 21 },
        { value: 1, unit: 'month', days: 30 },
        { value: 2, unit: 'month', days: 60 },
        { value: 3, unit: 'month', days: 90 },
        { value: 4, unit: 'month', days: 120 },
        { value: 6, unit: 'month', days: 180 },
      ] as SubscriptionIntervalOption[],
    },
    orderDetails: {
      labelKey: 'account.order-details',
      url: urls.order,
      isReturnComplaintEnabled: envBoolean(
        process.env.NEXT_PUBLIC_RETURN_COMPLAINT_ENABLED
      ),
      isEnabledB2BProductListView: isB2BStoreType,
      isEnabledDeliveryStatusSplit: false,
      saveAsFavoriteList: {
        isEnabled: false,
        icon: 'heart-empty',
      },
      b2bProductList: {
        // Definition of columns to be rendered on order product list
        columns: [
          { id: 'product' },
          { id: 'price' },
          { id: 'quantity' },
          { id: 'total' },
          { id: 'taxPrice' },
          { id: 'totalInclVat' },
        ],
      } as OrderProductListB2BConfig,
      invoiceList: {
        pageSize: 100, // Number of invoices fetched per order (no pagination UI)
        // Definition of columns to be rendered on order invoice list
        columns: [
          { id: 'date' },
          { id: 'invoiceNumber' },
          { id: 'total' },
          { id: 'download' },
        ],
      } as OrderInvoiceListConfig,
      b2bAddress: {
        footerFields: [],
      } as OrderAddressB2BConfig,
      orderSummaryPositionEnabled: false,
      orderSummaryPosition: isB2BStoreType ? 'top' : 'bottom',
      orderSummaryInvoicingDetailsEnabled: false,
      progressSteps: [
        { key: 'placed', label: 'Order placed' },
        { key: 'processed', label: 'Processed' },
        { key: 'sent', label: 'Sent' },
      ],
      pickupProgressSteps: [
        { key: 'placed', label: 'Order placed' },
        { key: 'processed', label: 'Processed' },
        { key: 'ready', label: 'Ready for pickup' },
      ],
    },
    cmsPlaceholders: [
      {
        pageType: PageType.MY_ACCOUNT,
        id: 'top',
        blockIdentifier: 'my-account-cms-block-top',
        skeletonHeight: 0,
      },
      {
        pageType: PageType.MY_ACCOUNT,
        id: 'loyalty-top',
        blockIdentifier: 'my-account-member-cms-block-top',
        skeletonHeight: 0,
      },
      {
        pageType: PageType.MY_ACCOUNT,
        id: 'bottom',
        blockIdentifier: 'my-account-cms-block',
        skeletonHeight: 0,
      },
      {
        pageType: PageType.MY_ACCOUNT,
        id: 'loyalty-bottom',
        blockIdentifier: 'my-account-member-cms-block',
        skeletonHeight: 0,
      },
      {
        pageType: PageType.MY_ACCOUNT,
        id: 'register-success-banner',
        blockIdentifier: 'register-success-banner',
        skeletonHeight: 0,
      },
      {
        pageType: PageType.ACCOUNT_INFORMATION,
        id: 'member-verification-message',
        blockIdentifier: 'account-overview-member-verification-cms-block',
        skeletonHeight: 0,
      },
      {
        pageType: PageType.ACCOUNT_INFORMATION,
        id: 'top',
        blockIdentifier: 'account-overview-cms-block',
        skeletonHeight: 0,
      },
      {
        pageType: PageType.ACCOUNT_INFORMATION,
        id: 'loyalty-top',
        blockIdentifier: 'account-overview-cms-block',
        skeletonHeight: 0,
      },
      {
        pageType: PageType.ACCOUNT_INFORMATION,
        id: 'bottom',
        blockIdentifier: 'account-overview-bottom',
        skeletonHeight: 0,
      },
      {
        pageType: PageType.ACCOUNT_INFORMATION,
        id: 'loyalty-bottom',
        blockIdentifier: 'account-overview-loyalty-bottom',
        skeletonHeight: 0,
      },
      {
        pageType: PageType.ACCOUNT_INFORMATION,
        id: 'change-password-slide-in',
        blockIdentifier: 'change-password-cms-block',
        skeletonHeight: 0,
      },
      {
        pageType: PageType.ACCOUNT_INFORMATION,
        id: 'delete-account-slide-in',
        blockIdentifier: 'delete-account-cms-block',
        skeletonHeight: 0,
      },
      {
        pageType: PageType.POINTS_HISTORY,
        id: 'top',
        blockIdentifier: 'bonus-points-history-top',
        skeletonHeight: 0,
      },
      {
        pageType: PageType.POINTS_HISTORY,
        id: 'bottom',
        blockIdentifier: 'bonus-points-history-bottom',
        skeletonHeight: 0,
      },
      {
        pageType: PageType.MEMBER_ADVANTAGES,
        id: 'top',
        blockIdentifier: 'member-advantages-top',
        skeletonHeight: 0,
      },
      {
        pageType: PageType.MEMBER_ADVANTAGES,
        id: 'bottom',
        blockIdentifier: 'member-advantages-bottom',
        skeletonHeight: 0,
      },
    ] as CmsPlaceholderAccountPage[],
  },
  profilePage: {
    consentConfigurations: [
      {
        id: 'EMAIL_MARKETING',
        inputType: 'checkbox',
        order: 1,
      },
      {
        id: 'SMS_MARKETING',
        inputType: 'checkbox',
        order: 2,
      },
      {
        id: 'DIGITAL_MARKETING',
        inputType: 'checkbox',
        order: 3,
      },
    ] as ConsentConfiguration[],
    isReadOnly: false,
  },
  internalCheckout: {
    // This array defines sections visibility and order
    sections: [
      'deliveryAddress',
      'invoicingAddress',
      'formFields',
      'deliveryOptions',
      'productList',
      'summary',
    ],
    deliveryAddress: { isChangeable: true, isShowMetadata: false },
    invoicingAddress: { isChangeable: true },
    formFields: {
      // This array defines form fields visibility and order
      fields: [
        { id: 'customer', isRequired: false },
        {
          id: 'orderConfirmationEmail',
          isRequired: false,
          isEmailPrefilled: false,
        },
        { id: 'reference', isRequired: false },
        { id: 'contactPerson', isRequired: false },
        { id: 'contactPhone', isRequired: false },
        { id: 'deliveryDate', isRequired: false },
        { id: 'comment', isRequired: false },
      ],
    },
    deliveryOptions: {
      // This array defines delivery-option visibility and order
      options: [
        { id: 'deliverTogether' },
        ...(isEnabledSubscriptions ? [{ id: 'subscription' }] : []),
        { id: 'futureDeliveryTime' },
      ],
    },
    summary: {
      paymentModeOptions: {
        invoice: ['invoice', 'card'],
        card: ['card'],
      } satisfies InternalCheckoutSummaryConfig['paymentModeOptions'],
    },
  } as InternalCheckoutConfig,
  article: {
    imagePlaceholder: placeholderNoImageSrc,
    preview: {
      maxDesktopItems: 3,
      maxMobileItems: 6,
    },
  },
  loginPage: {
    isSlidein: true,
    modalKey: 'login',
    showVippsButton: envBoolean(process.env.NEXT_PUBLIC_VIPPS_AUTH_ENABLED),
    successQueryParam: 'loginSuccess',
  },
  afterLogin: {
    redirectExcludedPaths: [urls.cart, urls.checkout],
    redirectToUrl: urls.myAccount,
  },
  auth: {
    protectedPaths: [
      urls.favorites,
      urls.invoices,
      urls.myAccount,
      urls.offers,
      urls.orders,
      urls.profile,
      urls.shoppingLists,
      urls.wishlist,
      ...(isEnabledSubscriptions ? [urls.subscriptions] : []),
    ],
  },
  registerPage: {
    isSlidein: !isB2BStoreType,
    modalKey: 'register',
    successQueryParam: 'registerSuccess',
    showVippsButton: envBoolean(process.env.NEXT_PUBLIC_VIPPS_AUTH_ENABLED),
    cmsPlaceholders: [
      {
        id: 'top',
        blockIdentifier: 'register-top',
        skeletonHeight: 0,
      },
      {
        id: 'loyalty-top',
        blockIdentifier: 'register-top',
        skeletonHeight: 0,
      },
      {
        id: 'membership-consent',
        blockIdentifier: 'membership-consent',
        skeletonHeight: 0,
      },
    ] as CmsPlaceholder[],
    consentConfigurations: [
      {
        id: 'EMAIL_MARKETING',
        inputType: 'checkbox',
        order: 1,
      },
      {
        id: 'SMS_MARKETING',
        inputType: 'checkbox',
        order: 2,
      },
      {
        id: 'DIGITAL_MARKETING',
        inputType: 'checkbox',
        order: 3,
      },
    ] as ConsentConfiguration[],
    vippsConsentConfigurations: [
      {
        id: 'MEMBERSHIP',
        inputType: 'toggle',
        order: 1,
      },
      {
        id: 'EMAIL_MARKETING',
        inputType: 'checkbox',
        order: 2,
      },
      {
        id: 'SMS_MARKETING',
        inputType: 'checkbox',
        order: 3,
      },
      {
        id: 'DIGITAL_MARKETING',
        inputType: 'checkbox',
        order: 4,
      },
    ] as ConsentConfiguration[],
    isCompanyRegistrationEnabled: isB2BStoreType,
  },
  resetPasswordPage: {
    modalKey: 'forgot-password',
    labelKey: 'auth.forgot-password.title',
  },
  googleMaps: {
    apiKey: process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY || '',
    mapId: process.env.NEXT_PUBLIC_GOOGLE_MAPS_MAP_ID || '',
    defaultCenter: { lat: 54.0021959912, lng: -2.54204416515 },
  },
  loyalty: {
    isLoyaltyEnabled: envBoolean(process.env.NEXT_PUBLIC_LOYALTY_ENABLED),
    accountPage: {
      menuItems: [
        {
          labelKey: 'account.orders',
          url: urls.orders,
          icon: 'package',
        },
        {
          labelKey: 'loyalty.member-advantages',
          url: urls.memberAdvantages,
          icon: 'percent-circle',
        },
        {
          labelKey: 'account.invoices',
          url: urls.invoices,
          icon: 'label',
        },
        ...(isEnabledSubscriptions
          ? [
              {
                labelKey: 'account.subscriptions',
                url: urls.subscriptions,
                icon: 'calendar',
              },
            ]
          : []),
        {
          labelKey:
            favoritesImplementation === 'simple'
              ? 'account.wishlist'
              : favoritesAdvancedVariant === 'b2b'
                ? 'account.shopping-lists'
                : 'account.favorites',
          url:
            favoritesImplementation === 'simple'
              ? urls.wishlist
              : favoritesAdvancedVariant === 'b2b'
                ? urls.shoppingLists
                : urls.favorites,
          icon: 'heart-empty',
        },
        {
          labelKey: 'account.profile',
          url: urls.profile,
          icon: 'user',
        },
        {
          labelKey: 'account.offers',
          url: urls.offers,
          icon: 'percent',
        },
      ],
      loyaltyHeaderUrl: urls.memberAdvantages,
      loyaltyPointsLimit: 10_000,
    },
    bonusPointsHistoryPage: {
      entriesPerPage: 6,
    },
  },
  favorites: {
    favoritesImplementation,
    favoritesAdvancedVariant,
    previewImagesLimit: favoritesAdvancedVariant === 'b2b' ? 1 : 4,
  },
}

export default theme
